import React, { useRef, useState, useEffect } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { UI_TIMINGS } from '../config/appConfig';
import './css/CameraCapture.css';

// Simple camera capture component
// - Requests camera permission via navigator.mediaDevices.getUserMedia
// - Shows live preview video
// - Captures a still image to a canvas and returns a data URL
// - Falls back to file input (accept image/* capture) for mobile devices

export default function CameraCapture({ onCapture, fileInputRef }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [error, setError] = useState(null);
  const [stream, setStream] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [flash, setFlash] = useState(false);

  // cleanup and stop camera when component unmounts or stream changes
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
      }
    };
  }, [stream]);

  // start camera automatically on mount
  useEffect(() => {
    startCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function startCamera() {
    setError(null);
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError('Camera API not available in this browser');
      return;
    }
    setIsStarting(true);
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
      setStream(s);
      if (videoRef.current) videoRef.current.srcObject = s;
      setIsRunning(true);
      setIsStarting(false);
    } catch (err) {
      console.error('getUserMedia error', err);
      setError(err.message || String(err));
      setIsStarting(false);
    }
  }

  function capture() {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    // flash animation
    setFlash(true);
    setTimeout(() => setFlash(false), UI_TIMINGS.CAMERA_FLASH_DURATION);
    const w = video.videoWidth || 640;
    const h = video.videoHeight || 480;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, w, h);
    const dataUrl = canvas.toDataURL('image/png');
    if (onCapture) onCapture(dataUrl);
    return dataUrl;
  }

  function onFileChange(e) {
    const f = e.target.files && e.target.files[0];
    if (!f) return;

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;

      // Convert all formats to PNG to ensure compatibility with AWS Rekognition
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const pngDataUrl = canvas.toDataURL('image/png');
        if (onCapture) onCapture(pngDataUrl);
      };
      img.onerror = (err) => {
        console.error('Failed to load image into Image element:', err);
        // Fall back to original data URL if conversion fails
        if (onCapture) onCapture(dataUrl);
      };
      img.src = dataUrl;
    };
    reader.onerror = (err) => {
      console.error('FileReader error:', err);
    };
    reader.readAsDataURL(f);
  }

  return (
    <div className="camera-capture-root">
      <div>
        {isStarting ? (
          <CircularProgress size={24} />
        ) : null}
      </div>

      <div className="camera-video-wrapper">
        <video
          ref={videoRef}
          className="camera-video"
          autoPlay
          playsInline
          muted
          onClick={() => { if (isRunning) capture(); }}
          title={isRunning ? 'Click to capture' : ''}
        />
        {flash && (
          <div className="camera-flash" />
        )}
      </div>

      {error && <div style={{ color: 'red' }}>Error: {error}</div>}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={onFileChange}
        style={{ display: 'none' }}
      />

      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
}
